import { DateRangeSearch } from '@routes/__root'
import { environmentIdsSearchSchema } from '@routes/-environmentIdsSearch'
import { protectWithWorkspacePermissions } from '@routes/-protectWithPermission'
import { withCompanyName } from '@routes/-withCompanyName'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { SmartSignalsPage } from 'features/smartSignals'
import { withCachedSearch } from 'helpers/withCachedSearch'
import { canViewAnalyticsPermission } from 'hooks/usePermission'
import { z } from 'zod/v4'

const cachedSearchParams = ['period', 'before', 'since'] as const

const platformSearchSchema = z.object({
  platform: z
    .array(z.enum(['ios', 'android', 'js']))
    .catch(['ios', 'android', 'js'])
    .default(['ios', 'android', 'js']),
  environmentIds: z.string().optional(),
})

type SmartSignalsSearch = z.infer<typeof platformSearchSchema>
type CombinedSearch = SmartSignalsSearch & DateRangeSearch
type CachedKeys = (typeof cachedSearchParams)[number]
type CachedSubset = Pick<CombinedSearch, CachedKeys>

export const Route = createFileRoute('/_authenticated/_layout/workspaces/$subscriptionId/smart-signals')({
  component: SmartSignalsPage,
  validateSearch: z.object({
    ...platformSearchSchema.shape,
    ...environmentIdsSearchSchema.shape,
  }),
  head: () => ({
    meta: [
      {
        title: withCompanyName('Smart Signals'),
      },
    ],
  }),
  beforeLoad: async (ctx) => {
    await protectWithWorkspacePermissions(canViewAnalyticsPermission)(ctx)

    const { search, location, params, cause } = ctx
    if (cause !== 'preload') {
      const cachedSearch = withCachedSearch<CachedSubset>({
        search,
        matchedRoute: location.pathname,
        cachedParamsWhitelist: cachedSearchParams,
        searchStr: location.searchStr,
      })

      if (cachedSearch) {
        throw redirect({
          to: '/workspaces/$subscriptionId/smart-signals',
          params,
          replace: true,
          search: (old) => ({
            ...old,
            platform: old.platform ?? ['ios', 'android', 'js'],
            ...cachedSearch,
          }),
        })
      }
    }
  },
})
